/**
 * VFXParticlesCore - Backend-agnostic particle system facade.
 */

import { resolveVFXParticlesBackend } from './VFXParticlesShared.js';
import { VFXParticlesWebGL } from './VFXParticlesWebGL.js';
import { VFXParticlesWebGPU } from './VFXParticlesWebGPU.js';

import type { VFXParticlesSettings } from '../types.js';
import type { vfxStore } from '../VFXStore.js';
import type {
  EmitCallbackSettings,
  EmitCallbackSettingsFn,
  VFXParticlesBackend,
  VFXParticlesConstructorOptions,
  VFXParticlesImplementation,
} from './VFXParticlesShared.js';
import type * as THREE from 'three';

export type {
  EmitCallbackSettings,
  EmitCallbackSettingsFn,
  VFXParticlesBackend,
  VFXParticlesConstructorOptions,
};

export class VFXParticlesCore {
  private readonly implementation: VFXParticlesImplementation;

  constructor(
    name: string,
    settings: VFXParticlesSettings = {},
    store?: ReturnType<typeof vfxStore.getState>,
    alphaMap?: THREE.Texture,
    geometry?: THREE.BufferGeometry,
    options?: VFXParticlesConstructorOptions
  ) {
    const backend = resolveVFXParticlesBackend(options);
    this.implementation = backend === 'webgl'
      ? new VFXParticlesWebGL(name, settings, store, alphaMap, geometry)
      : new VFXParticlesWebGPU(name, settings, store, alphaMap, geometry);
  }

  public getMesh(): THREE.InstancedMesh {
    return this.implementation.getMesh();
  }

  public update(time?: number): void {
    this.implementation.update(time);
  }

  public updateSettings(settings: VFXParticlesSettings): void {
    this.implementation.updateSettings(settings);
  }

  public dispose(): void {
    this.implementation.dispose();
  }
}
